import React, { useState, useEffect } from "react";
import { auth } from "../../firebase";
import { parseHTMLContent } from "../documentation/htmlParsingUtils";
import { Quill } from "react-quill-new";
import "../../utils/quillFonts";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";

// CSS imports for proper styling
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/ImportDocument.css";
import "../../css/QuillOverrides.css";


// Register Quill modules for proper processing
Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);

// Quill Core Imports for custom blots
const Inline = Quill.import("blots/inline");
const Block = Quill.import("blots/block");

// Custom Inline Code Block
class InlineCode extends Inline {
    static create() {
        const node = super.create();
        node.setAttribute("class", "inline-code");
        return node;
    }
    static formats(node) {
        return true;
    }
}
InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

// Custom Multi-line Blockquote
class MultiLineBlockquote extends Block {
    static create() {
        const node = super.create();
        node.setAttribute("class", "multi-line-blockquote");
        return node;
    }
    static formats(node) {
        return true;
    }
}
MultiLineBlockquote.blotName = "multi-line-blockquote";
MultiLineBlockquote.tagName = "blockquote";

// Custom Large Code Block
class LargeCodeBlock extends Block {
    static create() {
        const node = super.create();
        node.setAttribute("class", "large-code-block");
        return node;
    }
    static formats(node) {
        return true;
    }
}
LargeCodeBlock.blotName = "large-code-block";
LargeCodeBlock.tagName = "pre";

// Register custom blots
Quill.register(InlineCode, true);
Quill.register(MultiLineBlockquote, true);
Quill.register(LargeCodeBlock, true);

function ProcessWorkspaceSpaceImport({ extractedData, spaceConfig, onComplete, onBack, workspaceId }) {
    const [currentStep, setCurrentStep] = useState("processing");
    const [progress, setProgress] = useState(0);
    const [currentAction, setCurrentAction] = useState("Starting import...");
    const [processedPages, setProcessedPages] = useState([]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [uid, setUid] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            }
        });

        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (uid && spaceConfig && extractedData) {
            processSpaceImport();
        }
    }, [uid, spaceConfig, extractedData]);

    const processSpaceImport = async () => {
        try {
            setCurrentAction("Preparing space import...");
            setProgress(10);

            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            // Step 1: Create the space in the workspace
            setCurrentAction("Creating space in workspace...");
            setProgress(20);

            const spaceCreationResponse = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/create/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                        "Requesting-User-Uid": uid,
                    },
                    body: JSON.stringify({
                        name: spaceConfig.spaceName,
                        description: spaceConfig.spaceDescription || "",
                        author_uid: uid,
                    }),
                }
            );

            if (!spaceCreationResponse.ok) {
                const errorData = await spaceCreationResponse.json();
                throw new Error(`Failed to create space: ${errorData.message || "Unknown error"}`);
            }

            const spaceData = await spaceCreationResponse.json();
            
            const spaceId = spaceData.space_id;
            
            if (!spaceId) {
                throw new Error("Space ID not found in response");
            }

            setCurrentAction("Processing pages...");
            setProgress(30);

            // Step 2: Process and upload pages
            const pagesToProcess = spaceConfig.selectedPages || [];
            const totalPages = pagesToProcess.length;
            const processedPagesData = [];

            for (let i = 0; i < totalPages; i++) {
                const pageInfo = pagesToProcess[i];
                const pageProgress = 30 + (i / totalPages) * 50; // 30% to 80% for page processing
                
                setCurrentAction(`Processing page ${i + 1} of ${totalPages}: ${pageInfo.title}`);
                setProgress(pageProgress);

                try {
                    // Get the HTML content for this page
                    const htmlContent = extractedData.htmlFiles[pageInfo.filename];
                    if (!htmlContent) {
                        console.warn(`No HTML content found for page: ${pageInfo.filename}`);
                        continue;
                    }

                    // Parse HTML content to Quill delta
                    const delta = parseHTMLContent(htmlContent, extractedData.images);

                    // Prepare the page creation payload
                    const pagePayload = {
                        page_name: pageInfo.title,
                        description: "",
                        is_public: false,
                        completed: true,
                        content: JSON.stringify(delta),
                        space_id: spaceId,
                    };

                    // Add parent page relationship if it exists
                    if (pageInfo.parentId) {
                        pagePayload.parent_page_id = pageInfo.parentId;
                    }

                    // Create the page in the workspace space
                    const pageCreationResponse = await fetch(
                        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/create/`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                "Requesting-User-Uid": uid,
                            },
                            body: JSON.stringify(pagePayload),
                        }
                    );

                    if (!pageCreationResponse.ok) {
                        const errorData = await pageCreationResponse.json();
                        console.error(`Failed to create page ${pageInfo.title}:`, errorData);
                        continue;
                    }

                    const pageData = await pageCreationResponse.json();
                    processedPagesData.push({
                        ...pageData,
                        title: pageInfo.title,
                        originalFilename: pageInfo.filename,
                    });

                } catch (pageError) {
                    console.error(`Error processing page ${pageInfo.title}:`, pageError);
                    // Continue with other pages even if one fails
                }
            }

            // Step 3: Finalize import
            setCurrentAction("Finalizing import...");
            setProgress(90);

            setProcessedPages(processedPagesData);
            setProgress(100);
            setCurrentAction("Import completed successfully!");
            setIsCompleted(true);

            // Wait a moment then call completion callback
            setTimeout(() => {
                onComplete(spaceId, processedPagesData, spaceConfig.spaceName, uid);
            }, 2000);

        } catch (error) {
            console.error("Error during space import:", error);
            setError(error.message || "An error occurred during import");
            setCurrentAction("Import failed");
        }
    };

    const handleBackClick = () => {
        if (onBack && !isCompleted) {
            onBack();
        }
    };

    if (error) {
        return (
            <div className="ImportDocument__main-div">
                <div className="ImportDocument__content">
                    <h2>Import Failed</h2>
                    <p className="error-message" style={{ color: "red", marginTop: "20px" }}>
                        {error}
                    </p>
                    <p className="medium" style={{ marginTop: "20px" }}>
                        Please try again or contact support if the issue persists.
                    </p>
                    <button 
                        onClick={handleBackClick}
                        className="ImportDocument__back-button"
                        style={{ marginTop: "20px" }}
                    >
                        Back to Configuration
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="ImportDocument__main-div">
            <div className="ImportDocument__content">
                <h2>Processing Space Import</h2>
                
                <div className="import-progress-container" style={{ marginTop: "30px" }}>
                    <div className="progress-bar-container" style={{ 
                        width: "100%", 
                        height: "20px", 
                        backgroundColor: "#f0f0f0", 
                        borderRadius: "10px",
                        overflow: "hidden"
                    }}>
                        <div 
                            className="progress-bar" 
                            style={{ 
                                height: "100%", 
                                backgroundColor: "#4CAF50", 
                                width: `${progress}%`,
                                transition: "width 0.3s ease"
                            }}
                        />
                    </div>
                    <p className="progress-text" style={{ 
                        marginTop: "10px", 
                        fontSize: "14px", 
                        color: "#666"
                    }}>
                        {progress}% Complete
                    </p>
                </div>

                <div className="current-action" style={{ marginTop: "20px" }}>
                    <p className="medium">{currentAction}</p>
                </div>

                {processedPages.length > 0 && (
                    <div className="processed-pages-summary" style={{ marginTop: "30px" }}>
                        <h3>Processed Pages ({processedPages.length})</h3>
                        <div className="pages-list" style={{ 
                            maxHeight: "200px", 
                            overflowY: "auto",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            padding: "10px",
                            marginTop: "10px"
                        }}>
                            {processedPages.map((page, index) => (
                                <div key={index} className="page-item" style={{ 
                                    padding: "5px 0",
                                    borderBottom: index < processedPages.length - 1 ? "1px solid #eee" : "none"
                                }}>
                                    <span className="page-title" style={{ fontWeight: "500" }}>
                                        {page.title}
                                    </span>
                                    <span className="page-status" style={{ 
                                        marginLeft: "10px",
                                        color: "#4CAF50",
                                        fontSize: "12px"
                                    }}>
                                        ✓ Imported
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!isCompleted && (
                    <div className="import-actions" style={{ marginTop: "30px" }}>
                        <button 
                            onClick={handleBackClick}
                            className="ImportDocument__back-button"
                            disabled={currentStep === "processing"}
                        >
                            Back to Configuration
                        </button>
                    </div>
                )}

                {isCompleted && (
                    <div className="completion-message" style={{ 
                        marginTop: "30px",
                        padding: "20px",
                        backgroundColor: "#e8f5e8",
                        border: "1px solid #4CAF50",
                        borderRadius: "5px"
                    }}>
                        <p className="medium" style={{ color: "#2e7d32" }}>
                            Space import completed successfully! You will be redirected to your new space shortly.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProcessWorkspaceSpaceImport;
